import { Bbox } from '../types/extraction-result';
import { Word } from '../types/job';

/** Rectangle in PDF points (upright frame, origin top-left). */
export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** PDF points → natural pixels at the backend's render DPI. */
export function ptToPx(pt: number, dpi: number): number {
  return (pt * dpi) / 72;
}

export function hullOfWords(words: Word[]): Box {
  return {
    x0: Math.min(...words.map((w) => w.x0)),
    y0: Math.min(...words.map((w) => w.y0)),
    x1: Math.max(...words.map((w) => w.x1)),
    y1: Math.max(...words.map((w) => w.y1)),
  };
}

/**
 * Tight hull of the words whose y-center lies inside the row bbox — instead of
 * the full row width. Without matching words the row bbox stays.
 */
export function tightenToWords(bbox: Bbox, page: number, words: Word[]): Box {
  const [x0, y0, x1, y1] = bbox;
  const inside = words.filter(
    (w) => w.page === page && (w.y0 + w.y1) / 2 >= y0 && (w.y0 + w.y1) / 2 <= y1
  );
  if (!inside.length) return { x0, y0, x1, y1 };
  return hullOfWords(inside);
}

/** Words whose y-center lies inside the box AND that overlap it horizontally. */
export function wordsInBox(box: Box, page: number, words: Word[]): Word[] {
  return words.filter(
    (w) =>
      w.page === page &&
      (w.y0 + w.y1) / 2 >= box.y0 &&
      (w.y0 + w.y1) / 2 <= box.y1 &&
      w.x1 >= box.x0 &&
      w.x0 <= box.x1
  );
}

/** Text of the words a box covers, in reading order. */
export function textInBox(box: Box, page: number, words: Word[]): string {
  return wordsInBox(box, page, words)
    .slice()
    .sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0)
    .map((w) => w.text)
    .join(' ');
}
